/**
 * Fleet Report mode switcher.
 *
 * The four statuses are MODES, not filters — Aaron, 2026-08-23: "Fleet Report's
 * chip bar - should be mode switcher not filters". Each is a distinct view of
 * the fleet the user navigates between, so each gets its own route and the
 * canonical BreadcrumbDropdownMenu carries them. This module first shipped with
 * a hand-rolled row of clickable Chips above the toolbar (the bespoke-chrome
 * defect), and my first correction wrongly demoted them to a singleSelect
 * column filter — a mode is not a filter, and collapsing one into the other
 * loses the route.
 *
 * Mirrored slavishly from src/component/ComponentLayout.tsx, which is the
 * canonical instance of this pattern (Parts / Flying / Removed / Rotable /
 * Retired). Same imports, same Title-wraps-BreadcrumbHoverRoot structure, same
 * allRoutes-plus-breadcrumbMenu split so the parent crumb stays a plain link.
 */
import { useEffect, useState } from "react";

import { Button, Typography } from "@mui/material";
import { usePopupState } from "material-ui-popup-state/hooks";
import { Outlet, useLocation } from "react-router-dom";

import {
  BreadcrumbChevronIcon,
  BreadcrumbDropdownMenu,
  BreadcrumbHoverRoot,
  BreadcrumbMenuLinkItem,
} from "../components/BreadcrumbDropdownMenu";
import { BreadcrumbHeaderListing, Title } from "../components/Header";
import { LinkRouter } from "../components/Links";
import {
  breadcrumbCurrentLabelSx,
  breadcrumbCurrentTriggerSx,
  breadcrumbTitleSx,
} from "../components/dropdownMenuStyles";

import type { AeronetViewProps } from "../types";

export const breadcrumbMenu: { [key: string]: string } = {
  "/fleet/all": "All Statuses",
  "/fleet/overdue": "Overdue",
  "/fleet/due-soon": "Due Soon",
  "/fleet/normal": "Normal",
};

const allRoutes: { [key: string]: string } = {
  ...{ "/fleet": "Fleet Report" },
  ...breadcrumbMenu,
};

const FleetLayout: React.FC<AeronetViewProps> = () => {
  const location = useLocation();
  const popupState = usePopupState({
    variant: "popover",
    popupId: "fleetBreadcrumbMenu",
  });

  const [pathLinks, setPathLinks] = useState<string[]>([]);

  useEffect(() => {
    const _pathLinks: string[] = [];
    const paths = location.pathname.split("/");
    let pathLinkCount = 0;
    for (let i = 0; i < paths.length; i++) {
      let path = paths[i];
      const previousPath = _pathLinks[pathLinkCount - 1] ?? "";
      if (previousPath) {
        path = previousPath + "/" + path;
      } else if (path) {
        path = "/" + path;
      }
      if (path) {
        _pathLinks.push(path);
        pathLinkCount++;
      }
    }
    setPathLinks(_pathLinks);
  }, [location.pathname]);

  return (
    <>
      <BreadcrumbHeaderListing aria-label="breadcrumb">
        <Title sx={breadcrumbTitleSx}>
          {pathLinks.map((path) => {
            if (path === location.pathname) {
              return (
                <BreadcrumbHoverRoot key={path} popupState={popupState}>
                  <Button
                    endIcon={<BreadcrumbChevronIcon />}
                    sx={breadcrumbCurrentTriggerSx}
                  >
                    <Typography
                      variant="h2"
                      component="span"
                      sx={breadcrumbCurrentLabelSx}
                    >
                      {allRoutes[location.pathname]}
                    </Typography>
                  </Button>
                  <BreadcrumbDropdownMenu popupState={popupState}>
                    {Object.entries(breadcrumbMenu).map(([key, value]) => (
                      <BreadcrumbMenuLinkItem
                        key={key.split("/")[2]}
                        to={key}
                        label={value}
                        selected={location.pathname === key}
                        onClick={popupState.close}
                      />
                    ))}
                  </BreadcrumbDropdownMenu>
                </BreadcrumbHoverRoot>
              );
            }
            return (
              <LinkRouter to={path} key={path}>
                {allRoutes[path]} /
              </LinkRouter>
            );
          })}
        </Title>
      </BreadcrumbHeaderListing>
      <Outlet />
    </>
  );
};

export default FleetLayout;
